import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calculator, DollarSign, FileText, Zap, CheckCircle, Clock, Shield, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PayPerUse = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: DollarSign,
      title: "No Monthly Fees",
      description: "Only pay when you actually use the service. No recurring charges or hidden fees."
    },
    {
      icon: Zap,
      title: "Instant Processing",
      description: "Upload your PDF and get results in seconds. No waiting, no delays."
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Your documents are processed securely and immediately deleted after analysis."
    },
    {
      icon: CheckCircle,
      title: "Professional Quality",
      description: "AI-powered analysis with accuracy suitable for tax professionals."
    }
  ];

  const useCases = [
    {
      scenario: "Individual Freelancer",
      description: "2-page bank statement, quarterly processing",
      calculation: "$1 upload + $2 pages = $3 per quarter",
      annual: "$12/year",
      comparison: "vs $300+ for traditional tax software"
    },
    {
      scenario: "Small Business",
      description: "5-page statement, monthly processing",
      calculation: "$1 upload + $5 pages = $6 per month",
      annual: "$72/year",
      comparison: "vs $500+ for business tax software"
    },
    {
      scenario: "Consultant",
      description: "10-page statement, tax season only",
      calculation: "$1 upload + $10 pages = $11 once",
      annual: "$11/year",
      comparison: "vs $200+ for one-time tax prep"
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: "Upload Your PDF",
      description: "Upload your bank statement - any major bank format supported",
      cost: "$1 upload fee"
    },
    {
      step: 2,
      title: "AI Analysis",
      description: "Our AI processes and categorizes every transaction automatically",
      cost: "$1 per page"
    },
    {
      step: 3,
      title: "Download Results",
      description: "Get categorized transactions, tax summaries, and Schedule C data",
      cost: "Free download"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <DollarSign className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Pay-Per-Use Pricing
            </h1>
          </div>
          <p className="text-lg text-slate-600">
            Revolutionary pricing that puts you in control. Only pay for what you actually process.
          </p>
        </div>

        {/* Pricing Overview */}
        <Card className="shadow-xl border-2 border-blue-200 bg-blue-50 mb-12">
          <CardContent className="p-8 text-center">
            <h2 className="text-3xl font-bold text-blue-800 mb-4">Simple, Transparent Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">$1</h3>
                <p className="text-slate-600">Per PDF Upload</p>
                <p className="text-sm text-slate-500 mt-2">One-time fee per document</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calculator className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">$1</h3>
                <p className="text-slate-600">Per Page Processed</p>
                <p className="text-sm text-slate-500 mt-2">For detailed AI analysis</p>
              </div>
            </div>
            <div className="mt-8 p-4 bg-white rounded-lg">
              <p className="text-lg font-semibold text-slate-800 mb-2">Example: 5-page bank statement</p>
              <p className="text-2xl font-bold text-blue-600">$6 total</p>
              <p className="text-slate-600">($1 upload + $5 processing)</p>
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card className="shadow-lg border-0 mb-12">
          <CardHeader className="bg-green-50 border-b">
            <CardTitle className="flex items-center gap-3 text-green-800">
              <Zap className="w-6 h-6" />
              How Pay-Per-Use Works
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-8">
              {howItWorks.map((step, index) => (
                <div key={index} className="flex items-start gap-6">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {step.step}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-semibold text-slate-800 mb-2">{step.title}</h4>
                    <p className="text-slate-700 mb-2">{step.description}</p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 inline-block">
                      <span className="text-blue-800 font-medium">{step.cost}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Benefits */}
        <Card className="shadow-lg border-0 mb-12">
          <CardHeader className="bg-purple-50 border-b">
            <CardTitle className="text-purple-800">Why Pay-Per-Use is Better</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-2">{benefit.title}</h4>
                      <p className="text-slate-700 text-sm">{benefit.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Use Cases */}
        <Card className="shadow-lg border-0 mb-12">
          <CardHeader className="bg-orange-50 border-b">
            <CardTitle className="text-orange-800">Real-World Examples</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              {useCases.map((useCase, index) => (
                <div key={index} className="bg-white border border-slate-200 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-lg font-semibold text-slate-800 mb-2">{useCase.scenario}</h4>
                      <p className="text-slate-600 text-sm">{useCase.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">{useCase.annual}</div>
                      <div className="text-sm text-slate-500">annually</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                    <div className="text-slate-700">{useCase.calculation}</div>
                    <div className="text-green-600 font-medium">{useCase.comparison}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Comparison */}
        <Card className="shadow-xl border-0 mb-12">
          <CardHeader className="bg-slate-50 border-b">
            <CardTitle className="text-slate-800">Traditional Software vs Pay-Per-Use</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-800">Feature</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-800">Traditional Software</th>
                    <th className="text-center py-3 px-4 font-semibold text-blue-800">Tax Helper Pros</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-700">Monthly Cost</td>
                    <td className="py-3 px-4 text-center text-red-600">$25-50+</td>
                    <td className="py-3 px-4 text-center text-green-600 font-semibold">$0</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-700">Annual Commitment</td>
                    <td className="py-3 px-4 text-center text-red-600">Required</td>
                    <td className="py-3 px-4 text-center text-green-600 font-semibold">None</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-700">Processing 1 document</td>
                    <td className="py-3 px-4 text-center text-slate-600">~$25 (monthly fee)</td>
                    <td className="py-3 px-4 text-center text-blue-600 font-semibold">$3-15 (actual cost)</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-700">Data Storage</td>
                    <td className="py-3 px-4 text-center text-orange-600">Stored indefinitely</td>
                    <td className="py-3 px-4 text-center text-green-600 font-semibold">Immediately deleted</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-slate-700">Setup Time</td>
                    <td className="py-3 px-4 text-center text-slate-600">Hours of configuration</td>
                    <td className="py-3 px-4 text-center text-green-600 font-semibold">Ready in seconds</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Getting Started */}
        <Card className="shadow-xl border-2 border-green-200 bg-green-50">
          <CardContent className="p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <CreditCard className="w-8 h-8 text-green-600" />
              <h3 className="text-2xl font-bold text-green-800">Ready to Pay Only for What You Use?</h3>
            </div>
            <p className="text-green-700 text-lg mb-6">
              Start processing your documents today. No commitments, no monthly fees, just transparent pricing 
              that scales with your needs.
            </p>
            <div className="flex justify-center gap-4 mb-6">
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
              >
                Get Started Now
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/help')}
                className="border-green-600 text-green-600 hover:bg-green-50 px-8 py-3"
              >
                Learn More
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 text-green-700 text-sm">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>Instant Processing</span>
              </div>
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4" />
                <span>Bank-Level Security</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                <span>No Contracts</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/credit-packages')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            View Credit Packages
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PayPerUse;